// src/live/services/cloudflareSignaling.ts
// WebSocket client for Cloudflare Live Worker — replaces Firestore signaling
import { auth } from '../../lib/firebase';
import type { User } from 'firebase/auth';
import type { LiveDeliveryMode } from '../types/liveDelivery';
import type { LiveArchiveMode, LiveArchivePolicy } from '../types/liveArchive';
import type { RecordingState } from '../types/recording';
import { withTimeout } from '../utils/withTimeout';

// ── Server message types (mirrored from Worker) ─────────────────────────

export interface ParticipantInfo {
  oderId: string;
  displayName: string;
  photoURL?: string;
  isHost: boolean;
  hasVideo: boolean;
  hasAudio: boolean;
  isMutedByHost: boolean;
  isViewer: boolean;
  joinedAt: number;
  /**
   * Phase 2 Stage B — when a peer runs on the SFU path it populates this with
   * its Cloudflare Realtime session id. Other SFU peers use it to subscribe
   * to that peer's published tracks via `addSfuTracks(… location: 'remote' …)`.
   * Always absent for peers on the mesh path; in a mixed-mode room the SFU
   * peers only see media from other SFU peers.
   */
  sfuSessionId?: string;
}

export interface ServerChatMessage {
  type: 'chat';
  id: string;
  userId: string;
  username: string;
  photoURL?: string;
  content: string;
  timestamp: number;
  /** Set when this is a 1:1 participant DM. Absent ⇒ public chat. */
  to?: string;
  /** True when delivered to viewers as a read-only mirror (host enabled dmsPublic). */
  readOnly?: boolean;
}

export type ServerMessage =
  | { type: 'joined'; roomId: string; participants: ParticipantInfo[]; spotlightUserId: string | null; chatHistory: ServerChatMessage[]; layoutMode?: 'grid' | 'focus'; dmHistory?: Record<string, ServerChatMessage[]>; dmsPublic?: boolean }
  | { type: 'participants'; participants: ParticipantInfo[] }
  | { type: 'offer' | 'answer' | 'ice'; senderId: string; data: RTCSessionDescriptionInit | RTCIceCandidateInit }
  | ServerChatMessage
  | { type: 'room_ended' }
  | { type: 'spotlight'; targetId: string | null }
  | { type: 'mute'; targetId: string; muted: boolean }
  | { type: 'error'; code: string; message: string }
  | { type: 'heartbeat_ack' }
  | { type: 'reaction_burst'; counts: Record<string, number> }
  | { type: 'host_metrics_update'; metrics: { activeParticipants: number; peakParticipants: number; totalChats: number; totalReactions: number; chatVelocity: number; reactionRate: number; totalSupport: number; avgSessionDuration: number } }
  | { type: 'support'; userId: string; displayName: string; amount: number; message?: string; totalSupport: number; topSupporter: { uid: string; displayName: string; amount: number } | null }
  | { type: 'battle_update'; hostAId: string; hostBId: string; scoreA: number; scoreB: number; timerEnd: number; phase: 'waiting' | 'active' | 'ended' }
  | { type: 'battle_result'; winnerId: string | null; scoreA: number; scoreB: number }
  | { type: 'speak_request'; userId: string; displayName: string }
  | { type: 'speak_granted'; targetId: string; granted: boolean }
  | { type: 'product_pinned'; product: { id: string; name: string; imageUrl: string; price: string; url: string } }
  | { type: 'product_unpinned' }
  | { type: 'layout_mode'; mode: 'grid' | 'focus' }
  | { type: 'dms_public'; value: boolean };

// ── Configuration ───────────────────────────────────────────────────────

const LIVE_API_BASE = import.meta.env.VITE_LIVE_API_BASE || '';
const HTTP_REQUEST_TIMEOUT_MS = 20_000;
const WS_CONNECT_TIMEOUT_MS = 10_000;
const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 15_000;
const MAX_RECONNECT_ATTEMPTS = 5;
const HEARTBEAT_INTERVAL_MS = 25_000;

// ── Helper: get Firebase ID token ───────────────────────────────────────

async function getIdToken(timeoutMs = 7000): Promise<string> {
  const user = auth.currentUser;
  if (user) {
    return withTimeout(user.getIdToken(), timeoutMs, 'Firebase token request timed out');
  }

  // Wait for auth to initialize
  return new Promise<string>((resolve, reject) => {
    const timeout = setTimeout(() => reject(new Error('Auth timeout')), timeoutMs);
    const unsub = auth.onAuthStateChanged((u: User | null) => {
      clearTimeout(timeout);
      unsub();
      if (u) {
        withTimeout(u.getIdToken(), timeoutMs, 'Firebase token request timed out')
          .then(resolve)
          .catch(reject);
      } else {
        reject(new Error('Not authenticated'));
      }
    });
  });
}

// ── HTTP helpers ────────────────────────────────────────────────────────

interface CreateRoomOptions {
  title: string;
  description?: string;
  roomType: 'meeting' | 'native' | 'shorts' | 'event' | 'battle' | 'audio';
  isVertical?: boolean;
  shortsMode?: 'broadcast' | 'interactive';
  broadcastMeeting?: boolean;
  deliveryMode?: LiveDeliveryMode;
  archiveMode?: LiveArchiveMode;
  archivePolicy?: LiveArchivePolicy;
  isPrivate?: boolean;
  maxParticipants?: number;
  category?: string;
  tags?: string[];
  thumbnailUrl?: string;
  hostName?: string;
  hostAvatar?: string | null;
  password?: string;
  allowedUsers?: string[];
  opponentId?: string;
  battleDuration?: number;
  requiredTier?: string;
  enableRecording?: boolean;
}

export interface CloudflareRoom {
  id: string;
  hostId: string;
  hostName?: string;
  hostAvatar?: string | null;
  title: string;
  description: string;
  roomType: string;
  isVertical: boolean;
  isPrivate: boolean;
  isActive: boolean;
  maxParticipants: number;
  category: string;
  tags: string[];
  thumbnailUrl: string;
  spotlightUserId: string | null;
  participants: number;
  startedAt: number;
  wsUrl: string;
  requiredTier?: string | null;
  shortsMode?: string;
  broadcastMeeting?: boolean;
  deliveryMode?: LiveDeliveryMode;
  archiveMode?: LiveArchiveMode;
  archivePolicy?: LiveArchivePolicy;
  hlsPlaybackUrl?: string;
  dashPlaybackUrl?: string;
  recording?: RecordingState;
}

export async function createRoom(options: CreateRoomOptions): Promise<CloudflareRoom> {
  const token = await getIdToken();

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), HTTP_REQUEST_TIMEOUT_MS);

  try {
    const res = await fetch(`${LIVE_API_BASE}/live/rooms`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(options),
      signal: controller.signal,
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error((err as { error?: string }).error || `HTTP ${res.status}`);
    }

    return (await res.json()) as CloudflareRoom;
  } finally {
    clearTimeout(timeout);
  }
}

export async function getRoomState(roomId: string): Promise<CloudflareRoom | null> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), HTTP_REQUEST_TIMEOUT_MS);
  try {
    const res = await fetch(`${LIVE_API_BASE}/live/rooms/${roomId}`, { signal: controller.signal });
    if (res.status === 404) return null;
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error((err as { error?: string }).error || `HTTP ${res.status}`);
    }
    return (await res.json()) as CloudflareRoom;
  } finally {
    clearTimeout(timeout);
  }
}

export async function getWhipUrl(roomId: string): Promise<string | null> {
  const token = await getIdToken();
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), HTTP_REQUEST_TIMEOUT_MS);
  try {
    const res = await fetch(`${LIVE_API_BASE}/live/rooms/${roomId}/whip-url`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { whipUrl?: string | null };
    return data.whipUrl || null;
  } catch {
    return null;
  } finally {
    clearTimeout(timeout);
  }
}

/**
 * LIVE-045 — Look up the auto-generated Video UID for the live broadcast in
 * progress. WHIP-ingested CF Stream live inputs do not serve HLS at the
 * Live-Input-ID URL; the playable manifest lives at the Video-ID URL of the
 * auto-recording asset, which appears ~5-15s after WHIP connect. The viewer
 * polls this until non-null, then constructs `<origin>/<videoUid>/manifest/
 * video.m3u8?dvrEnabled=true` from the existing `room.hlsPlaybackUrl` origin.
 * Returns null while the asset hasn't appeared yet OR when auth fails.
 */
export async function getLiveVideoUid(roomId: string): Promise<string | null> {
  const token = await getIdToken();
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), HTTP_REQUEST_TIMEOUT_MS);
  try {
    const res = await fetch(`${LIVE_API_BASE}/live/rooms/${roomId}/live-video-uid`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { liveVideoUid?: string | null };
    return data.liveVideoUid || null;
  } catch {
    return null;
  } finally {
    clearTimeout(timeout);
  }
}

/**
 * Fetch RTMPS ingest credentials for external-encoder broadcasters.
 * Host-only — Worker enforces auth + ownership.
 * Returns null when the room isn't ready, recording isn't enabled, or auth fails.
 */
export async function getRtmpsCredentials(
  roomId: string,
): Promise<{ rtmpsUrl: string; streamKey: string } | null> {
  const token = await getIdToken();
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), HTTP_REQUEST_TIMEOUT_MS);
  try {
    const res = await fetch(`${LIVE_API_BASE}/live/rooms/${roomId}/rtmps-url`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { rtmpsUrl?: string; streamKey?: string };
    if (!data.rtmpsUrl || !data.streamKey) return null;
    return { rtmpsUrl: data.rtmpsUrl, streamKey: data.streamKey };
  } catch {
    return null;
  } finally {
    clearTimeout(timeout);
  }
}

// ── Phase 2 Stage B — CF Realtime SFU client helpers ───────────────────────

export interface SfuSession {
  sessionId: string;
  appId: string;
}

/** Client track descriptor passed to `addSfuTracks`. Mirrors the shape the
 *  Worker expects; see `cloudflare/live-worker/src/cloudflareCalls.ts`. */
export type SfuTrackDescriptor =
  | { location: 'local'; mid: string; trackName: string }
  | { location: 'remote'; sessionId: string; trackName: string; mid?: string };

export interface AddSfuTracksResponse {
  sessionDescription?: { type: 'answer'; sdp: string };
  tracks: Array<{
    mid?: string;
    trackName?: string;
    sessionId?: string;
    error?: string;
    errorDescription?: string;
  }>;
}

/**
 * Ask the Worker to create a fresh CF Realtime SFU session for this room.
 * Returns null when the Worker replies with `SFU_NOT_CONFIGURED` (503) so the
 * dispatcher can fall back to the mesh path.
 */
export async function createSfuSession(roomId: string): Promise<SfuSession | null> {
  const token = await getIdToken();
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), HTTP_REQUEST_TIMEOUT_MS);
  try {
    const res = await fetch(`${LIVE_API_BASE}/live/rooms/${roomId}/sfu-session`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    });
    if (!res.ok) return null;
    const data = (await res.json()) as Partial<SfuSession>;
    if (!data.sessionId || !data.appId) return null;
    return { sessionId: data.sessionId, appId: data.appId };
  } catch {
    return null;
  } finally {
    clearTimeout(timeout);
  }
}

/**
 * Proxy an SDP offer + track descriptors through the Worker to CF Realtime.
 * The Worker keeps the App Secret. Used for both publishing local tracks
 * (`location: 'local'`) and subscribing to remote tracks (`location: 'remote'`).
 */
export async function addSfuTracks(
  roomId: string,
  sessionId: string,
  offerSdp: string,
  tracks: SfuTrackDescriptor[],
): Promise<AddSfuTracksResponse | null> {
  const token = await getIdToken();
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), HTTP_REQUEST_TIMEOUT_MS);
  try {
    const res = await fetch(`${LIVE_API_BASE}/live/rooms/${roomId}/sfu-session/tracks/new`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ sessionId, offerSdp, tracks }),
      signal: controller.signal,
    });
    if (!res.ok) return null;
    return (await res.json()) as AddSfuTracksResponse;
  } catch {
    return null;
  } finally {
    clearTimeout(timeout);
  }
}

export async function requestTusUpload(
  roomId: string,
  fileSize: number,
): Promise<{ tusUrl: string; videoUid: string } | null> {
  const token = await getIdToken();
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), HTTP_REQUEST_TIMEOUT_MS);
  try {
    const res = await fetch(`${LIVE_API_BASE}/live/rooms/${roomId}/tus-upload`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ fileSize }),
      signal: controller.signal,
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { tusUrl?: string; videoUid?: string };
    if (!data.tusUrl || !data.videoUid) return null;
    return { tusUrl: data.tusUrl, videoUid: data.videoUid };
  } catch {
    return null;
  } finally {
    clearTimeout(timeout);
  }
}

// Host-scoped TUS upload NOT tied to any live room. Used by the
// "Publish a video you already have" flow on /studio/replays.
export async function requestHostTusUpload(
  fileSize: number,
  title?: string,
): Promise<{ tusUrl: string; videoUid: string } | null> {
  const token = await getIdToken();
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), HTTP_REQUEST_TIMEOUT_MS);
  try {
    const res = await fetch(`${LIVE_API_BASE}/live/host/tus-upload`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ fileSize, title }),
      signal: controller.signal,
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { tusUrl?: string; videoUid?: string };
    if (!data.tusUrl || !data.videoUid) return null;
    return { tusUrl: data.tusUrl, videoUid: data.videoUid };
  } catch {
    return null;
  } finally {
    clearTimeout(timeout);
  }
}

export interface ReplayVideoStatus {
  ready: boolean;
  state: string | null;
  playbackUrl: string;
  thumbnailUrl: string;
  durationSec: number;
}

export async function getReplayVideoStatus(
  videoUid: string,
): Promise<ReplayVideoStatus | null> {
  const token = await getIdToken();
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), HTTP_REQUEST_TIMEOUT_MS);
  try {
    const res = await fetch(`${LIVE_API_BASE}/live/videos/${videoUid}/status`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    });
    if (!res.ok) return null;
    return (await res.json()) as ReplayVideoStatus;
  } catch {
    return null;
  } finally {
    clearTimeout(timeout);
  }
}

export async function endRoomHTTP(roomId: string): Promise<void> {
  const token = await getIdToken();

  const res = await fetch(`${LIVE_API_BASE}/live/rooms/${roomId}/end`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (res.status === 404 || res.status === 410) {
    // The host_end WebSocket path can finish room teardown before this HTTP
    // safety-net call lands. Treat missing/inactive rooms as already ended.
    return;
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error((err as { error?: string }).error || `HTTP ${res.status}`);
  }
}

// ── RoomConnection class — single WebSocket per room ────────────────────

export type RoomEventHandler = {
  onJoined?: (data: { roomId: string; participants: ParticipantInfo[]; spotlightUserId: string | null; chatHistory: ServerChatMessage[]; layoutMode?: 'grid' | 'focus'; dmHistory?: Record<string, ServerChatMessage[]>; dmsPublic?: boolean }) => void;
  onParticipants?: (participants: ParticipantInfo[]) => void;
  onSignal?: (signal: { type: 'offer' | 'answer' | 'ice'; senderId: string; data: RTCSessionDescriptionInit | RTCIceCandidateInit }) => void;
  onChat?: (msg: ServerChatMessage) => void;
  onRoomEnded?: () => void;
  onSpotlight?: (targetId: string | null) => void;
  onMute?: (targetId: string, muted: boolean) => void;
  onError?: (code: string, message: string) => void;
  onConnectionStateChange?: (state: 'connecting' | 'connected' | 'disconnected' | 'failed') => void;
  onReactionBurst?: (counts: Record<string, number>) => void;
  onHostMetrics?: (metrics: { activeParticipants: number; peakParticipants: number; totalChats: number; totalReactions: number; chatVelocity: number; reactionRate: number; totalSupport: number; avgSessionDuration: number }) => void;
  onSupport?: (data: { userId: string; displayName: string; amount: number; message?: string; totalSupport: number; topSupporter: { uid: string; displayName: string; amount: number } | null }) => void;
  onLayoutMode?: (mode: 'grid' | 'focus') => void;
  onDmsPublicChange?: (value: boolean) => void;
};

export class RoomConnection {
  private ws: WebSocket | null = null;
  private roomId: string;
  private handlers: RoomEventHandler;
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  private reconnectAttempts = 0;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private intentionalClose = false;
  private _isConnected = false;
  private joinPayload: { displayName: string; isHost: boolean; photoURL?: string; viewerMode?: boolean } | null = null;
  private connectPromise: Promise<void> | null = null;

  // Typed event registration registry
  private customHandlers = new Map<string, Set<(data: any) => void>>();

  constructor(roomId: string, handlers: RoomEventHandler) {
    this.roomId = roomId;
    this.handlers = handlers;
  }

  // ── Typed event registration API ──────────────────────────────────────

  on(type: string, handler: (data: any) => void): void {
    if (!this.customHandlers.has(type)) {
      this.customHandlers.set(type, new Set());
    }
    this.customHandlers.get(type)!.add(handler);
  }

  off(type: string, handler: (data: any) => void): void {
    this.customHandlers.get(type)?.delete(handler);
  }

  get isConnected(): boolean {
    return this._isConnected;
  }

  async connect(): Promise<void> {
    this.intentionalClose = false;
    this.cancelReconnect();

    if (this.ws?.readyState === WebSocket.OPEN) {
      this._isConnected = true;
      return;
    }

    if (this.connectPromise) {
      return this.connectPromise;
    }

    this.handlers.onConnectionStateChange?.('connecting');

    this.connectPromise = (async () => {
      try {
        const token = await getIdToken();
        const wsBase = LIVE_API_BASE.replace(/^http/, 'ws');
        const wsUrl = `${wsBase}/live/rooms/${this.roomId}/ws?token=${encodeURIComponent(token)}`;

        await this.connectWebSocket(wsUrl);
      } catch (err) {
        console.error('[RoomConnection] Connect failed:', err);
        this.handlers.onConnectionStateChange?.('failed');
        this.scheduleReconnect();
      } finally {
        this.connectPromise = null;
      }
    })();

    return this.connectPromise;
  }

  private connectWebSocket(wsUrl: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const connectTimeout = setTimeout(() => {
        ws.close();
        reject(new Error('WebSocket connect timeout'));
      }, WS_CONNECT_TIMEOUT_MS);

      const ws = new WebSocket(wsUrl);
      this.ws = ws;

      ws.onopen = () => {
        clearTimeout(connectTimeout);
        if (this.ws !== ws) {
          try { ws.close(1000, 'Superseded connection'); } catch {}
          return;
        }
        this._isConnected = true;
        this.reconnectAttempts = 0;
        this.cancelReconnect();
        this.handlers.onConnectionStateChange?.('connected');
        this.startHeartbeat();
        if (this.joinPayload) {
          this.send({ type: 'join', ...this.joinPayload });
        }
        resolve();
      };

      ws.onmessage = (event: MessageEvent) => {
        if (this.ws !== ws) return;
        this.handleMessage(event.data as string);
      };

      ws.onclose = () => {
        clearTimeout(connectTimeout);
        if (this.ws !== ws) return;
        this.ws = null;
        this._isConnected = false;
        this.stopHeartbeat();
        this.handlers.onConnectionStateChange?.('disconnected');

        if (!this.intentionalClose) {
          this.scheduleReconnect();
        }
      };

      ws.onerror = () => {
        clearTimeout(connectTimeout);
        if (this.ws !== ws) return;
        // onclose will fire after onerror
      };
    });
  }

  private handleMessage(raw: string): void {
    let msg: ServerMessage;
    try {
      msg = JSON.parse(raw) as ServerMessage;
    } catch {
      return;
    }

    // Dispatch to registered custom handlers before built-in handling
    const handlers = this.customHandlers.get(msg.type);
    if (handlers) {
      for (const handler of handlers) handler(msg);
    }

    switch (msg.type) {
      case 'joined':
        this.handlers.onJoined?.(msg);
        break;
      case 'participants':
        this.handlers.onParticipants?.(msg.participants);
        break;
      case 'offer':
      case 'answer':
      case 'ice':
        this.handlers.onSignal?.(msg);
        break;
      case 'chat':
        this.handlers.onChat?.(msg);
        break;
      case 'room_ended':
        this.handlers.onRoomEnded?.();
        this.close(); // Don't reconnect after room ends
        break;
      case 'spotlight':
        this.handlers.onSpotlight?.(msg.targetId);
        break;
      case 'mute':
        this.handlers.onMute?.(msg.targetId, msg.muted);
        break;
      case 'error':
        this.handlers.onError?.(msg.code, msg.message);
        break;
      case 'heartbeat_ack':
        // Connection is alive
        break;
      case 'reaction_burst':
        this.handlers.onReactionBurst?.(msg.counts);
        break;
      case 'host_metrics_update':
        this.handlers.onHostMetrics?.(msg.metrics);
        break;
      case 'support':
        this.handlers.onSupport?.(msg);
        break;
      case 'layout_mode':
        this.handlers.onLayoutMode?.(msg.mode);
        break;
      case 'dms_public':
        this.handlers.onDmsPublicChange?.(msg.value);
        break;
    }
  }

  send(msg: object): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    }
  }

  // ── Typed send helpers ─────────────────────────────────────────────────

  sendJoin(displayName: string, isHost: boolean, photoURL?: string, viewerMode?: boolean): void {
    this.joinPayload = { displayName, isHost, photoURL, viewerMode };
    this.send({ type: 'join', ...this.joinPayload });
  }

  sendLeave(): void {
    this.send({ type: 'leave' });
  }

  sendOffer(recipientId: string, data: RTCSessionDescriptionInit): void {
    this.send({ type: 'offer', recipientId, data });
  }

  sendAnswer(recipientId: string, data: RTCSessionDescriptionInit): void {
    this.send({ type: 'answer', recipientId, data });
  }

  sendIce(recipientId: string, data: RTCIceCandidateInit): void {
    this.send({ type: 'ice', recipientId, data });
  }

  /**
   * Send a chat message.
   * - No `opts.to` → public broadcast (existing behavior).
   * - `opts.to` set → 1:1 participant DM. Server validates that both sides
   *   are non-viewer participants and routes the message accordingly.
   */
  sendChat(content: string, opts?: { to?: string }): void {
    if (opts?.to) {
      this.send({ type: 'chat', content, to: opts.to });
    } else {
      this.send({ type: 'chat', content });
    }
  }

  /** Host-only: toggle whether viewers receive participant DM mirrors as read-only. */
  sendSetDmsPublic(value: boolean): void {
    this.send({ type: 'set_dms_public', value });
  }

  sendHostEnd(): void {
    this.send({ type: 'host_end' });
  }

  sendMute(targetId: string, muted: boolean): void {
    this.send({ type: 'mute', targetId, muted });
  }

  sendKick(targetId: string): void {
    this.send({ type: 'kick', targetId });
  }

  sendSpotlight(targetId: string | null): void {
    this.send({ type: 'spotlight', targetId });
  }

  sendMediaUpdate(hasVideo: boolean, hasAudio: boolean): void {
    this.send({ type: 'media_update', hasVideo, hasAudio });
  }

  sendReaction(emoji: string): void {
    this.send({ type: 'reaction', emoji });
  }

  sendSupport(data: { userId: string; displayName: string; amount: number; message?: string }): void {
    this.send({ type: 'support', ...data });
  }

  sendBattleJoin(): void {
    this.send({ type: 'battle_join' });
  }

  sendBattleStart(): void {
    this.send({ type: 'battle_start' });
  }

  sendRequestSpeak(): void {
    this.send({ type: 'request_speak' });
  }

  sendGrantSpeak(targetId: string, granted: boolean): void {
    this.send({ type: 'grant_speak', targetId, granted });
  }

  sendPinProduct(product: { id: string; name: string; imageUrl: string; price: string; url: string }): void {
    this.send({ type: 'pin_product', ...product });
  }

  sendUnpinProduct(): void {
    this.send({ type: 'unpin_product' });
  }

  sendLayoutMode(mode: 'grid' | 'focus'): void {
    this.send({ type: 'layout_mode', mode });
  }

  /**
   * Phase 2 Stage B — advertise our CF Realtime SFU session id to the DO
   * so other SFU peers can subscribe to our published tracks.
   */
  sendSfuSession(sessionId: string): void {
    this.send({ type: 'sfu_session', sessionId });
  }

  // ── Lifecycle ──────────────────────────────────────────────────────────

  close(): void {
    this.intentionalClose = true;
    this.stopHeartbeat();
    this.cancelReconnect();
    this.connectPromise = null;

    if (this.ws) {
      try { this.ws.close(1000, 'Client close'); } catch {}
      this.ws = null;
    }

    this._isConnected = false;
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatInterval = setInterval(() => {
      this.send({ type: 'heartbeat' });
    }, HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.intentionalClose || this.reconnectTimeout) return;

    this.reconnectAttempts++;
    if (this.reconnectAttempts > MAX_RECONNECT_ATTEMPTS) {
      console.warn(
        `[CloudflareSignaling] Giving up after ${MAX_RECONNECT_ATTEMPTS} reconnect attempts`
      );
      this.handlers.onConnectionStateChange?.('disconnected');
      return;
    }
    const baseDelay = Math.min(
      RECONNECT_BASE_MS * Math.pow(2, this.reconnectAttempts - 1),
      RECONNECT_MAX_MS,
    );
    // ±30% jitter prevents thundering herd when many clients reconnect after a Worker restart
    const jitter = baseDelay * 0.3 * (Math.random() * 2 - 1);
    const delay = Math.max(100, Math.round(baseDelay + jitter));

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = null;
      void this.connect();
    }, delay);
  }

  private cancelReconnect(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }
}
